import { useEffect, useState } from 'react';
import { loadTinyMCE } from './MedicalCertificateDialog';

const defaultOptions = {
    height: 'auto',
    spellchecker_language: 'en',
    browser_spellcheck: true,
    statusbar: false,
    menubar: false,
    plugins: ['advlist', 'lists', 'paste'],
    toolbar: 'bold italic bullist',
    toolbar_location: 'auto',
    toolbar_persist: false,
    content_style:
        '#tinymce { font-family:Helvetica,Arial,sans-serif; font-size:13px; margin-top: 4px; } #tinymce p, #tinymce ul{ margin: 0 0;}',
    entity_encoding: 'named',
};

type Props = {
    id: string;
    inputValue?: string;
    onInputChange?: (text: string, parsedText: string) => void;
    onSave?: (text: string, parsedText: string) => void;
    initialValue?: string;
    onBlur?: (text: string, parsedText: string) => void;
    customOptions?: { [key: string]: any };
    parentCssClasses?: string;
    fullScreenHeightCssClass?: number;
    isFullScreen?: boolean;
    toolbarId?: string;
    max_chars?: number;
    onFullScreenToggle?: () => void;
};

const TinyMCEditor: React.FC<Props> = ({
    id,
    inputValue,
    onInputChange,
    onSave,
    initialValue,
    onBlur,
    customOptions,
    parentCssClasses,
    fullScreenHeightCssClass,
    isFullScreen,
    toolbarId,
    max_chars = Number.MAX_SAFE_INTEGER,
    onFullScreenToggle,
}) => {
    const [fullScreen, setFullScreen] = useState(false);

    const initEditor = (): void => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        tinymce?.init({
            selector: `#${id}`,
            ...defaultOptions,
            ...(customOptions || []),
            paste_as_text: true,
            height: fullScreen
                ? fullScreenHeightCssClass
                : customOptions?.height || defaultOptions.height,
            fixed_toolbar_container: `#${toolbarId}`,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setup: (editor) => {
                editor?.on('init', function () {
                    editor?.setContent(initialValue || '');
                });

                const allowedKeys = [8, 37, 38, 39, 40, 46];

                editor.on('keydown', function (e: any) {
                    if (allowedKeys.indexOf(e.keyCode) != -1) return true;

                    if (editor.getContent().length + 1 > max_chars) {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                    return true;
                });
                const onChange = (e: any) => {
                    // this line checks if only \n (\n or enter etc... treated as empty) which leaves behind when we clear editor
                    if (e?.target?.innerText?.trim() !== '') {
                        onInputChange?.(e?.target?.innerHTML, e?.target?.innerText);
                    } else {
                        onInputChange?.('', '');
                    }
                };

                const onBlurSave = (e: any) => {
                    e.stopImmediatePropagation();
                    const htmlContent = e?.target?.getContent?.();
                    const textContent = e?.target?.getContent?.({ format: 'text' });
                    if (htmlContent !== null && htmlContent !== undefined) {
                        onBlur?.(htmlContent, textContent);
                    }
                };

                editor.on('focus', function () {
                    editor?.selection?.select(editor?.getBody(), true);
                    editor?.selection?.collapse(false);
                });

                editor.on('blur', onBlurSave);

                editor.on('keyup', onChange);
            },

            paste_preprocess: function (_: any, args: any) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const editor = tinymce.get(id);
                const len = editor.getContent().length;
                const argsLength = args.content.length;

                if (len + argsLength > max_chars) {
                    alert(
                        'Pasting this exceeds the maximum allowed number of ' +
                            max_chars +
                            ' characters.',
                    );
                    args.content = '';
                }
            },
        });
    };

    useEffect(() => {
        async function loadTinyMceScript() {
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (tinymce) {
                    initEditor();
                } else {
                    await loadTinyMCE();
                    initEditor();
                }
            } catch (e) {
                await loadTinyMCE();
                initEditor();
            }
        }
        loadTinyMceScript();
        return () => {
            try {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                tinymce?.get(id)?.destroy();
            } catch (e) {
                console.error(e);
            }
        };
    }, [initialValue, fullScreen]);

    const onClickSave = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const tinyMceById = tinymce?.get(id);

        onSave?.(tinyMceById.getContent(), tinyMceById.getContent({ format: 'text' }));
        tinyMceById?.setContent('');
        setFullScreen(false);
    };

    const onToggleFullScreen = () => {
        if (onFullScreenToggle) {
            onFullScreenToggle();
        } else {
            setFullScreen(!fullScreen);
        }
    };

    return (
        <div className="relative">
            <div
                id={toolbarId}
                className={`${
                    parentCssClasses ||
                    'flex flex-col tiny-mce overflow-auto border border-neutral-7 rounded-4 bg-white p-10 hide-scrollbar cursor-text focus-within:border-darwin-neutral-400 focus:outline-none max-h-96 min-h-52'
                }`}
                onClick={() =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    tinymce?.get(id)?.focus()
                }
            >
                <div className="flex-1">
                    <div id={id}></div>
                </div>

                {inputValue && (
                    <div className="absolute flex justify-end tick chip-show mr-8 right-0 bottom-4">
                        <div
                            onClick={onClickSave}
                            className="flex items-center justify-center ripple cursor-pointer"
                        >
                            <svg
                                style={{ pointerEvents: 'auto' }}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                width="16"
                                height="16"
                                className="fill-actionPrimary chip-show"
                            >
                                <path d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z"></path>
                            </svg>
                        </div>
                    </div>
                )}
                {isFullScreen && (
                    <div
                        className="absolute flex justify-end cursor-pointer right-10 top-32"
                        onClick={onToggleFullScreen}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            className="w-14 h-14"
                        >
                            <path d="M32 64C14.3 64 0 78.3 0 96v96c0 17.7 14.3 32 32 32s32-14.3 32-32V128h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 320c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V320zM384 64c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V96c0-17.7-14.3-32-32-32H384zM512 320c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H384c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V320z" />
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TinyMCEditor;
